// Bottom sheet for message actions: quick reactions on top, then one row per action.
// Same on iOS and Android (Alert caps Android at three buttons, which is why this exists).
import React, { useRef } from 'react';
import { Modal, View, Pressable, StyleSheet, Platform } from 'react-native';
import { Text } from './Text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { radius, makeStyles, useTheme } from '../theme';

export type SheetAction = { label: string; onPress: () => void; destructive?: boolean; hint?: string };

export function ActionSheet({ visible, title, subtitle, reactions, onReact, actions, onClose }: {
  visible: boolean; title?: string; subtitle?: string;
  reactions?: string[]; onReact?: (key: string) => void;
  actions: SheetAction[]; onClose: () => void;
}) {
  const theme = useTheme(); const s = useStyles();
  const insets = useSafeAreaInsets();
  // Run the chosen action only after the sheet has actually closed. On iOS a picker (photo library,
  // files, camera) cannot be presented while this modal is still animating away — it fails silently.
  const pending = useRef<null | (() => void)>(null);
  const runPending = () => { const fn = pending.current; pending.current = null; fn?.(); };
  const choose = (fn: () => void) => {
    onClose();
    if (Platform.OS === 'ios') { pending.current = fn; setTimeout(runPending, 700); }   // onDismiss fires first; the timer is the fallback
    else if (Platform.OS === 'android') setTimeout(fn, 150);
    else fn();
  };
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} onDismiss={runPending}>
      <Pressable style={s.backdrop} onPress={onClose}>
        <Pressable style={[s.sheet, { paddingBottom: Math.max(insets.bottom, 12) }]} onPress={() => {}}>
          <View style={s.grip} />
          {(!!title || !!subtitle) && (
            <View style={s.head}>
              {!!title && <Text style={s.title} numberOfLines={1}>{title}</Text>}
              {!!subtitle && <Text style={s.sub} numberOfLines={2}>{subtitle}</Text>}
            </View>
          )}
          {!!reactions?.length && (
            <View style={s.rxRow}>
              {reactions.map((k) => (
                <Pressable key={k} onPress={() => { onReact?.(k); onClose(); }} style={({ pressed }) => [s.rx, pressed && { backgroundColor: theme.surface2 }]} accessibilityLabel={`React ${k}`}>
                  <Text style={s.rxText}>{k}</Text>
                </Pressable>
              ))}
            </View>
          )}
          {actions.map((a, i) => (
            <Pressable key={`${i}-${a.label}`} onPress={() => choose(a.onPress)} style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]}>
              <Text style={[s.rowText, a.destructive && { color: theme.deny }]}>{a.label}</Text>
              {!!a.hint && <Text style={s.rowHint}>{a.hint}</Text>}
            </Pressable>
          ))}
          <Pressable onPress={onClose} style={({ pressed }) => [s.row, s.cancel, pressed && { opacity: 0.6 }]}>
            <Text style={[s.rowText, { color: theme.muted, textAlign: 'center' }]}>Cancel</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: theme.backdrop, justifyContent: 'flex-end' },
  sheet: { backgroundColor: theme.surface, borderTopLeftRadius: radius.xl, borderTopRightRadius: radius.xl, paddingTop: 8, paddingHorizontal: 12, borderWidth: 1, borderColor: theme.border },
  grip: { alignSelf: 'center', width: 36, height: 4, borderRadius: 2, backgroundColor: theme.border, marginBottom: 8 },
  head: { paddingHorizontal: 8, paddingVertical: 6 },
  title: { color: theme.ink, fontWeight: '700', fontSize: 14 },
  sub: { color: theme.muted, fontSize: 13, marginTop: 2 },
  rxRow: { flexDirection: 'row', justifyContent: 'space-around', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: theme.border, marginBottom: 4 },
  rx: { width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center' },
  rxText: { fontSize: 28 },
  row: { paddingVertical: 14, paddingHorizontal: 8, borderRadius: radius.md },
  rowText: { color: theme.ink, fontSize: 16, fontWeight: '600' },
  rowHint: { color: theme.faint, fontSize: 12.5, marginTop: 2 },
  cancel: { marginTop: 4, backgroundColor: theme.surface2 },
}));
